import { createSelector } from 'reselect'
import { get, partialRight } from 'lodash'
import { REA, LO } from 'src/constants/agent_types'
import { fullName } from './utils'
import { selectDisableYlopoHack } from './environment'

export const selectCustomerProfileLoading = state =>
  state.customerProfile.loading

export const selectCustomerProfile = state => get(state, 'customerProfile.data')

export const selectCustomerPhone = state => {
  const cp = selectCustomerProfile(state) || {}

  return getCustomerPhone(cp)
}

const select = getter =>
  createSelector(
    selectCustomerProfile,
    getter
  )

const getFromCustomerProfile = key => partialRight(get, key)

export const getCustomerId = getFromCustomerProfile('id')
export const getCustomerHasSponsor = getFromCustomerProfile('hasSponsor')
export const getCustomerEmail = getFromCustomerProfile('email')
export const getCustomerFirstName = getFromCustomerProfile('firstName')
export const getCustomerLastName = getFromCustomerProfile('lastName')
export const getCustomerPhotoUri = getFromCustomerProfile('photoUri')
export const getCustomerNmls = getFromCustomerProfile('nmls')
export const getCustomerType = getFromCustomerProfile('type')
export const getCustomerDisclaimer = getFromCustomerProfile('disclaimer')
export const getCustomerFeatures = getFromCustomerProfile('features')
export const getCustomerListingsUri = getFromCustomerProfile('listingsUri')
export const getCustomerCorporationName = getFromCustomerProfile(
  'corporationName'
)
export const getCustomerBuyersEnabled = getFromCustomerProfile('buyersEnabled')
export const getCustomerLegacyRef = getFromCustomerProfile('legacyRef')

export const selectCustomerId = select(getCustomerId)
export const selectCustomerHasSponsor = select(getCustomerHasSponsor)
export const selectCustomerEmail = select(getCustomerEmail)
export const selectCustomerFirstName = select(getCustomerFirstName)
export const selectCustomerLastName = select(getCustomerLastName)
export const selectCustomerPhotoUri = select(getCustomerPhotoUri)
export const selectCustomerNMLS = select(getCustomerNmls)
export const selectCustomerType = select(getCustomerType)
export const selectCustomerDisclaimer = select(getCustomerDisclaimer)
export const selectCustomerCorporationName = select(getCustomerCorporationName)
export const selectListingsUri = select(getCustomerListingsUri)
export const selectFeatures = select(getCustomerFeatures)
export const selectCustomerBuyersEnabled = select(getCustomerBuyersEnabled)
export const selectCustomerLegacyRef = select(getCustomerLegacyRef)

export const getCustomerPhone = profile => {
  return get(profile, 'mobilePhone') || get(profile, 'workPhone')
}

export const selectFullName = createSelector(
  selectCustomerFirstName,
  selectCustomerLastName,
  fullName
)

export const selectFeatureSet = createSelector(
  selectFeatures,
  features => {
    if (!features) {
      return undefined
    }

    const featureSet = new Set()
    for (let i = 0; i < features.length; i++) {
      featureSet.add(features[i])
    }
    return featureSet
  }
)

const createFeatureFlagSelector = flag =>
  createSelector(
    selectFeatureSet,
    features => features && features.has(flag)
  )

export const selectHasMortgageCoach = createFeatureFlagSelector('mortgageCoach')

const selectHasYlopoListingsFeature = createFeatureFlagSelector('ylopoListings')
export const selectDisplayYlopoSpecificContent = createSelector(
  selectHasYlopoListingsFeature,
  selectDisableYlopoHack,
  (hasFeature, disabled) => hasFeature && !disabled
)

export const selectName = createSelector(
  selectCustomerFirstName,
  selectCustomerLastName,
  (first, last) => first && last && `${first} ${last}`
)

export const selectListingsUriForZipcode = zipcode =>
  createSelector(
    selectListingsUri,
    uri => uri && uri.replace('{zipcode}', zipcode)
  )

export const selectOfficeProfile = state => {
  const cp = selectCustomerProfile(state) || {}
  return cp.officeProfile
}

export const selectFromOfficeProfile = key =>
  createSelector(
    selectOfficeProfile,
    op => get(op, key)
  )

export const selectOfficeAddress = selectFromOfficeProfile('address')

export const selectFromOfficeAddress = key =>
  createSelector(
    selectOfficeAddress,
    address => get(address, key)
  )

export const selectOfficeZip = selectFromOfficeAddress('zipCode')

export const selectCorporateProfile = state => {
  const op = selectOfficeProfile(state) || {}
  return op.corporateProfile
}

const selectFromCorporateProfile = key =>
  createSelector(
    selectCorporateProfile,
    partialRight(get, key)
  )

export const selectCompanyFeatures = selectFromCorporateProfile('features')
export const selectCompanyName = selectFromCorporateProfile('name')

export const selectCompanyFeatureSet = createSelector(
  selectCompanyFeatures,
  features => {
    if (!features) {
      return undefined
    }
    const featureSet = new Set()
    for (let i = 0; i < features.length; i++) {
      featureSet.add(features[i])
    }
    return featureSet
  }
)

const createCompanyFeatureFlagSelector = flag =>
  createSelector(
    selectCompanyFeatureSet,
    features => features && features.has(flag)
  )

export const selectCompanyHasWINMortgagePaymentOptions = createCompanyFeatureFlagSelector(
  'winMortgagePaymentOptions'
)

export const selectCustomerIsREA = createSelector(
  selectCustomerType,
  type => type === REA
)

export const selectCustomerIsLO = createSelector(
  selectCustomerType,
  type => type === LO
)

export const selectREA = createSelector(
  selectCustomerIsREA,
  selectCustomerProfile,
  (isREA, cp) => (isREA ? cp : null)
)
